import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button, Table, Badge, Spinner, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import { previewSlotEntryCascade, applySlotEntryCascade } from "state/rotationSlots/actions";
import { frDate } from "./slotLabels";

const STATUS_LABELS = { 101: "En attente", 102: "Validée" };

/**
 * Synchronisation des devis déjà créés après une bascule de calendrier :
 * preview (devis 101/102 dont la livraison tombe dans la fenêtre de l'entrée)
 * puis confirmation. Remplace l'item de l'entrée précédente par celui de
 * l'entrée dans les forfaits porteurs du slot, reprice et re-sync VosFactures.
 */
const CascadeModal = ({ show, onHide, entry, slotName, onApplied }) => {
  const dispatch = useDispatch();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!entry) return;
    setLoading(true);
    setPreview(null);
    const resp = await dispatch(previewSlotEntryCascade(entry.id));
    setLoading(false);
    if (!resp || resp.error || resp.success === false) {
      toast.error(resp?.message || "Erreur lors de la prévisualisation");
      return;
    }
    setPreview(resp);
  }, [dispatch, entry]);

  useEffect(() => {
    if (show) loadPreview();
  }, [show, loadPreview]);

  const handleApply = async () => {
    if (!entry || applying) return;
    setApplying(true);
    const resp = await dispatch(applySlotEntryCascade(entry.id));
    setApplying(false);
    if (!resp || resp.error || resp.success === false) {
      toast.error(resp?.message || "Erreur lors de la cascade");
      return;
    }
    const counts = resp.counts || {};
    toast.success(
      `Cascade appliquée : ${counts.orders || 0} devis mis à jour (${counts.orderArticles || 0} lignes), re-sync VosFactures en file.`
    );
    onApplied && onApplied();
    onHide();
  };

  const context = preview?.context;
  const orders = preview?.orders || [];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Synchroniser les devis existants{slotName ? ` — ${slotName}` : ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center my-4">
            <Spinner animation="border" size="sm" /> Calcul de l'impact…
          </div>
        )}

        {!loading && preview && !context && (
          <Alert variant="secondary" className="mb-0">
            Rien à cascader pour cette entrée : pas d'entrée précédente avec un item différent,
            ou aucun forfait rattaché au slot.
          </Alert>
        )}

        {!loading && context && (
          <>
            <p className="mb-2">
              <strong>{context.sourceName}</strong> → <strong>{context.targetName}</strong>
              <span className="text-muted">
                {" "}— livraisons du {frDate(context.windowFrom)}
                {context.windowUntil ? ` au ${frDate(context.windowUntil)} (exclu)` : " et au-delà"}
              </span>
            </p>
            <Alert variant="info" className="py-2">
              Seuls les devis <strong>en attente / validés</strong> (non annulés) des forfaits
              rattachés au slot sont concernés. Les devis créés après la saisie du calendrier
              sont déjà résolus à l'ajout.
            </Alert>

            {orders.length === 0 && <p className="text-muted mb-0">Aucun devis à mettre à jour.</p>}

            {orders.length > 0 && (
              <>
                <p className="mb-2">
                  <strong>{orders.length}</strong> devis impactés (
                  {preview.counts?.orderArticles || 0} lignes) :
                </p>
                <div style={{ maxHeight: "45vh", overflowY: "auto" }}>
                  <Table striped bordered hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Devis</th>
                        <th>Livraison</th>
                        <th>Statut</th>
                        <th>Lignes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((o) => (
                        <tr key={o.id}>
                          <td>{o.numbering || `#${o.id}`}</td>
                          <td>{frDate(o.deliveryDate)}</td>
                          <td>
                            <Badge variant={o.status === "102" ? "success" : "warning"}>
                              {STATUS_LABELS[o.status] || o.status}
                            </Badge>
                          </td>
                          <td>{o.articles}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={applying}>
          Fermer
        </Button>
        <Button
          variant="primary"
          onClick={handleApply}
          disabled={loading || applying || !orders.length}
        >
          {applying ? "Application…" : `Mettre à jour ${orders.length} devis`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CascadeModal;
